import React, { useEffect, useState } from "react";
import { Tent, MapPin, Calendar, Phone, Plus, X, Loader2, ExternalLink, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PURPOSES = [
  { k: "b2b",             l: "B2B" },
  { k: "citizens",        l: "Citizens" },
  { k: "disaster_relief", l: "Disaster Relief" },
  { k: "social_welfare",  l: "Social Welfare Distribution" },
  { k: "health",          l: "Health Camp" },
  { k: "education",       l: "Education / Scholarship Camp" },
  { k: "recruitment",     l: "Recruitment Drive" },
];

const PURPOSE_STYLE = {
  b2b:              "bg-indigo-100 text-indigo-800",
  citizens:         "bg-emerald-100 text-emerald-800",
  disaster_relief:  "bg-red-100 text-red-800",
  social_welfare:   "bg-amber-100 text-amber-800",
  health:           "bg-sky-100 text-sky-800",
  education:        "bg-fuchsia-100 text-fuchsia-800",
  recruitment:      "bg-stone-100 text-stone-800",
};

export default function Camps() {
  const { user } = useAuth();
  const { loc, detect } = useUserLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purpose, setPurpose] = useState("");
  const [q, setQ] = useState("");
  const [activeNow, setActiveNow] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useSeo({
    title: "Planned Exhibition & Welfare Camps · find2near",
    description: "Every planned booth, exhibition, disaster-relief and social-welfare camp with live GPS, permit references and helpdesk contacts.",
    canonical: "/camps",
  });

  const load = () => {
    setLoading(true);
    const params = { limit: 60 };
    if (purpose)   params.purpose = purpose;
    if (q)         params.q = q;
    if (activeNow) params.active_now = true;
    if (loc.lat && loc.lng) { params.lat = loc.lat; params.lng = loc.lng; params.radius_km = 25; }
    api.get("/camps", { params })
       .then((r) => setItems(r.data))
       .catch(() => setItems([]))
       .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [purpose, activeNow, loc.lat, loc.lng]); // eslint-disable-line

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6" data-testid="camps-page">
      <CampForm open={showForm} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />

      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><Tent size={12} className="text-fuchsia-600" /> Planned camps · booths · stalls</div>
          <h1 className="heading text-3xl md:text-4xl font-black text-stone-900">Every camp. <span className="text-fuchsia-600">Every booth.</span></h1>
          <p className="text-stone-500 text-sm mt-1">B2B exhibitions · Citizen camps · Disaster relief · Social welfare distributions.</p>
        </div>
        {user && (
          <Button onClick={() => setShowForm(true)} className="rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 font-black" data-testid="camp-new-btn">
            <Plus size={14} className="mr-1.5" /> Register camp
          </Button>
        )}
      </div>

      <div className="bg-white border border-stone-200 rounded-3xl p-3 flex flex-wrap gap-2 items-center mb-4">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search camps…" className="rounded-full h-9 flex-1 min-w-[180px] text-sm" data-testid="camp-q" />
        <Select value={purpose || "__all__"} onValueChange={(v) => setPurpose(v === "__all__" ? "" : v)}>
          <SelectTrigger className="rounded-full h-9 w-52 text-xs" data-testid="camp-purpose"><SelectValue placeholder="All purposes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All purposes</SelectItem>
            {PURPOSES.map((p) => <SelectItem key={p.k} value={p.k}>{p.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="text-xs font-bold text-stone-700 inline-flex items-center gap-1.5 px-3 h-9 rounded-full border border-stone-200">
          <input type="checkbox" checked={activeNow} onChange={(e) => setActiveNow(e.target.checked)} data-testid="camp-active" />
          Active now
        </label>
        <button onClick={() => detect()} className="text-xs font-bold text-emerald-700 hover:bg-emerald-50 rounded-full px-3 h-9">
          <MapPin size={12} className="inline mr-1" /> My location
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16 text-stone-400"><Loader2 className="mx-auto animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-stone-400 border border-dashed border-stone-200 rounded-3xl">No camps in this filter.</div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {items.map((c) => (
            <div key={c.camp_id} className="rounded-3xl bg-white border border-stone-200 p-4" data-testid={`camp-card-${c.camp_id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className={`text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 ${PURPOSE_STYLE[c.purpose] || "bg-stone-100 text-stone-800"}`}>{c.purpose.replace(/_/g, " ")}</span>
                {c.booth_no && <span className="text-[10px] font-mono font-black bg-stone-100 rounded-full px-2 py-0.5">Booth #{c.booth_no}</span>}
                {c.verified && <span className="text-[10px] font-black text-emerald-700 inline-flex items-center gap-0.5"><ShieldCheck size={10} /> verified</span>}
                {c.distance_km != null && <span className="text-[10px] text-stone-500">· {c.distance_km} km</span>}
              </div>
              <div className="font-black text-stone-900 text-lg">{c.name}</div>
              <div className="text-[11px] text-stone-500 flex flex-wrap items-center gap-3 mt-0.5">
                <span className="inline-flex items-center gap-1"><Calendar size={10} /> {c.start_at.slice(0, 16).replace("T", " ")} → {c.end_at.slice(0, 16).replace("T", " ")}</span>
                {c.city && <span className="inline-flex items-center gap-1"><MapPin size={10} /> {c.city}</span>}
              </div>
              {c.address && <div className="text-xs text-stone-600 mt-1 line-clamp-2">{c.address}</div>}
              {c.description && <p className="text-sm text-stone-700 mt-2 line-clamp-3">{c.description}</p>}
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                {c.permission_ref && <span className="text-stone-600">Permit: <b className="font-mono">{c.permission_ref}</b></span>}
                {c.permission_doc && <a href={c.permission_doc} target="_blank" rel="noopener noreferrer" className="text-sky-700 inline-flex items-center gap-1 hover:underline"><ExternalLink size={10} /> View permit</a>}
                {c.helpdesk_phone && <a href={`tel:${c.helpdesk_phone}`} className="text-emerald-700 inline-flex items-center gap-1 font-bold"><Phone size={11} /> {c.helpdesk_name || c.helpdesk_phone}</a>}
              </div>
              {c.location?.lat && (
                <a href={`https://www.google.com/maps?q=${c.location.lat},${c.location.lng}`} target="_blank" rel="noopener noreferrer"
                   className="mt-2 inline-flex text-[11px] font-bold text-fuchsia-700 hover:underline items-center gap-1">
                  <MapPin size={11} /> Open in maps
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const emptyCamp = () => ({
  name: "", purpose: "citizens", booth_no: "",
  address: "", city: "", lat: "", lng: "",
  start_at: "", end_at: "",
  permission_ref: "", permission_doc: "",
  helpdesk_name: "", helpdesk_phone: "",
  description: "",
});

function CampForm({ open, onClose, onSaved }) {
  const { loc } = useUserLocation();
  const [f, setF] = useState(emptyCamp());
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const set = (patch) => setF((s) => ({ ...s, ...patch }));

  const pickHere = () => set({ lat: String(loc.lat || ""), lng: String(loc.lng || "") });

  const submit = async () => {
    if (f.name.trim().length < 2) { toast.error("Name is required"); return; }
    if (!f.start_at || !f.end_at) { toast.error("Start & end times are required"); return; }
    setBusy(true);
    try {
      await api.post("/camps", {
        ...f,
        lat: f.lat === "" ? null : parseFloat(f.lat),
        lng: f.lng === "" ? null : parseFloat(f.lng),
      });
      toast.success("Camp registered");
      setF(emptyCamp());
      onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="camp-form-modal">
      <div className="bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-gradient-to-r from-fuchsia-50 to-white">
          <div className="flex items-center gap-2"><Tent className="text-fuchsia-700" size={16} /><b>Register a camp / booth / stall</b></div>
          <button onClick={onClose} className="text-stone-500 hover:text-stone-900"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-2 max-h-[70vh] overflow-y-auto">
          <Input value={f.name} onChange={(e) => set({ name: e.target.value })} placeholder="Camp / stall name" className="rounded-2xl h-10" />
          <div className="grid grid-cols-2 gap-2">
            <Select value={f.purpose} onValueChange={(v) => set({ purpose: v })}>
              <SelectTrigger className="rounded-2xl h-10"><SelectValue /></SelectTrigger>
              <SelectContent>{PURPOSES.map((p) => <SelectItem key={p.k} value={p.k}>{p.l}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={f.booth_no} onChange={(e) => set({ booth_no: e.target.value })} placeholder="Booth / Stall #" className="rounded-2xl h-10 font-mono" />
          </div>
          <Input value={f.address} onChange={(e) => set({ address: e.target.value })} placeholder="Address" className="rounded-2xl h-10" />
          <div className="grid grid-cols-4 gap-2">
            <Input value={f.city} onChange={(e) => set({ city: e.target.value })} placeholder="City" className="rounded-2xl h-10 col-span-2" />
            <Input value={f.lat} onChange={(e) => set({ lat: e.target.value })} placeholder="Lat" className="rounded-2xl h-10 font-mono" />
            <Input value={f.lng} onChange={(e) => set({ lng: e.target.value })} placeholder="Lng" className="rounded-2xl h-10 font-mono" />
          </div>
          <button type="button" onClick={pickHere} className="text-[11px] font-bold text-emerald-700 hover:underline"><MapPin size={11} className="inline mr-1" /> Use my current GPS</button>
          <div className="grid grid-cols-2 gap-2">
            <Input type="datetime-local" value={f.start_at} onChange={(e) => set({ start_at: e.target.value })} className="rounded-2xl h-10 font-mono" />
            <Input type="datetime-local" value={f.end_at}   onChange={(e) => set({ end_at: e.target.value })}   className="rounded-2xl h-10 font-mono" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input value={f.permission_ref} onChange={(e) => set({ permission_ref: e.target.value })} placeholder="Permission ref no." className="rounded-2xl h-10 font-mono" />
            <Input value={f.permission_doc} onChange={(e) => set({ permission_doc: e.target.value })} placeholder="Permission doc URL" className="rounded-2xl h-10 font-mono" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input value={f.helpdesk_name}  onChange={(e) => set({ helpdesk_name: e.target.value })}  placeholder="Helpdesk name" className="rounded-2xl h-10" />
            <Input value={f.helpdesk_phone} onChange={(e) => set({ helpdesk_phone: e.target.value })} placeholder="Helpdesk phone" className="rounded-2xl h-10 font-mono" />
          </div>
          <textarea value={f.description} onChange={(e) => set({ description: e.target.value })} rows={3} placeholder="What happens at this camp?" className="w-full px-4 py-3 rounded-2xl border border-stone-200 outline-none text-sm" />
          <Button onClick={submit} disabled={busy} className="w-full rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 font-black">
            {busy ? <Loader2 className="animate-spin mr-2" size={14} /> : null} Register camp
          </Button>
        </div>
      </div>
    </div>
  );
}
